import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";

import { Container } from "@roqua/components/container";
import { Section, SectionHeader } from "@roqua/components/sections";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@roqua/components/ui/accordion";
import { Badge, TaggedBadge } from "@roqua/components/ui/badge";
import { Button } from "@roqua/components/ui/button";
import { Skeleton } from "@roqua/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@roqua/components/ui/tooltip";

import { ErrorFallback } from "@/components/ErrorBoundary";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { PageTitle } from "@/components/PageTitle";

export const Route = createFileRoute("/$organizationKey/$dossierId/design/components")({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <>
      <PageTitle title="designComponents" />
      <Section variant="header">
        <SectionHeader header="Header section variant" />
        <Examples />
      </Section>

      <Section variant="middle">
        <SectionHeader header="Middle section variant" />
        <div className="flex items-center gap-2">
          <header className="flex-1 text-2xl">Middle</header>
        </div>
        <Examples />
      </Section>

      <Section>
        <SectionHeader header="Content section variant" />
        <Examples />
        <Container>
          <Examples />
        </Container>
      </Section>
    </>
  );
}

const Examples = () => {
  return (
    <div className="flex flex-col gap-8">
      <div>
        <header className="mb-4 font-mono text-xl">Button</header>

        <div className="grid grid-cols-2 gap-4">
          <Button>Default</Button>
          <Button disabled>Default</Button>

          <Button variant="link">Link</Button>
          <Button variant="link" disabled>
            Link
          </Button>

          <Button variant="destructive">Destructive</Button>
          <Button variant="destructive" disabled>
            Destructive
          </Button>

          <Button variant="outline">Outline</Button>
          <Button variant="outline" disabled>
            Outline disabled
          </Button>

          <Button variant="ghost">Ghost</Button>
          <Button variant="ghost" disabled>
            Ghost disabled
          </Button>

          <Button variant="hero">Hero</Button>
          <Button variant="hero" disabled>
            Hero disabled
          </Button>
        </div>
      </div>

      <div>
        <header className="mb-4 font-mono text-xl">Badge</header>

        <div className="flex gap-2">
          <Badge interactive>Default</Badge>
          <Badge variant="muted" interactive>
            Muted
          </Badge>
          <Badge variant="destructive" interactive>
            Destructive
          </Badge>
          <Badge variant="error" interactive>
            Error
          </Badge>
          <Badge variant="warning" interactive>
            Warning
          </Badge>
          <Badge variant="outline" interactive>
            Outline
          </Badge>
        </div>
      </div>

      <div>
        <header className="mb-4 font-mono text-xl">TaggedBadge</header>

        <div className="flex gap-2">
          <TaggedBadge tag="Variant" interactive>
            Default
          </TaggedBadge>
          <TaggedBadge tag="Variant" variant="muted" interactive>
            Muted
          </TaggedBadge>
          <TaggedBadge tag="Variant" variant="destructive" interactive>
            Destructive
          </TaggedBadge>
          <TaggedBadge tag="Variant" variant="error" interactive>
            Error
          </TaggedBadge>
          <TaggedBadge tag="Variant" variant="warning" interactive>
            Warning
          </TaggedBadge>
          <TaggedBadge tag="Variant" variant="outline" interactive>
            Outline
          </TaggedBadge>
        </div>
      </div>

      <div>
        <header className="mb-4 font-mono text-xl">Tooltip</header>
        <Tooltip>
          <TooltipTrigger>
            <Button>Dit heeft een tooltip</Button>
          </TooltipTrigger>
          <TooltipContent>En dit is de tooltip</TooltipContent>
        </Tooltip>
      </div>

      <div>
        <header className="mb-4 font-mono text-xl">Accordion</header>
        <Accordion type="single">
          <AccordionItem value="first">
            <AccordionTrigger>Eerste item</AccordionTrigger>
            <AccordionContent>
              <p>Lipsum</p>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="second">
            <AccordionTrigger>Tweede item</AccordionTrigger>
            <AccordionContent>
              <p>Lipsum</p>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="last">
            <AccordionTrigger>Hoeveel items moet ik nog verzinnen?</AccordionTrigger>
            <AccordionContent>
              <p>Lipsum</p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      <div>
        <header className="mb-4 font-mono text-xl">Sonner toasts</header>

        <div className="flex gap-2">
          <Button onClick={() => toast("Message")}>Default</Button>
          <Button onClick={() => toast.success("yaay!")}>Success</Button>
          <Button onClick={() => toast.warning("uhm")}>Warning</Button>
          <Button onClick={() => toast.error("whoops")}>Error</Button>
          <Button
            onClick={() =>
              toast.success("Something was done", {
                action: { label: "Undo", onClick: () => toast("Undone") },
              })
            }
          >
            With Action
          </Button>
          <Button
            onClick={() =>
              toast.promise(new Promise((resolve) => setTimeout(resolve, 2000)), {
                loading: "Loading...",
                success: "Done!",
              })
            }
          >
            For Promise
          </Button>
        </div>
      </div>

      <div>
        <header className="mb-4 font-mono text-xl">Skeleton</header>
        <Skeleton className="h-4 w-32" />
      </div>

      <div>
        <header className="mb-4 font-mono text-xl">Spinner</header>
        <LoadingSpinner />
      </div>

      <div>
        <header className="mb-4 font-mono text-xl">ErrorFallback</header>
        <ErrorFallback error={new Error("test")} resetErrorBoundary={() => null} />
      </div>
    </div>
  );
};
